import { SortingState } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { getViewStateForLocation, LocationViewState, setPatientViewState } from '../../stores/slices/patient-view';

type DataTableState = {
  page?: number;
  sorting?: SortingState;
  searchTerm?: string;
};

type DataTableStateHook = [
  DataTableState,
  (sorting?: SortingState) => void,
  (page: number) => void,
  (searchTerm?: string) => void,
  () => void
];

export const usePatientViewDataTableState = (
  defaultState?: DataTableState,
  dataTableName?: string
): DataTableStateHook => {
  const dispatch = useDispatch();
  const location = useLocation();
  const alias = dataTableName ? `#${dataTableName}` : location.hash;
  const [defaultViewState] = useState(defaultState || {});
  const viewState = useSelector(getViewStateForLocation(location.pathname + alias));

  const shouldResetState = location.state?.resetView === true;
  const [state, setState] = useState<DataTableState>(
    shouldResetState ? defaultViewState : viewState?.dataTableView || defaultViewState
  );

  useEffect(() => {
    const viewState: LocationViewState = {
      location: location.pathname + alias,
      viewState: {
        dataTableView: {
          page: state.page || defaultState?.page,
          sorting: state.sorting || defaultState?.sorting,
          searchTerm: state.searchTerm || defaultState?.searchTerm
        }
      }
    };

    dispatch(setPatientViewState(viewState));
  }, [alias, defaultState?.page, defaultState?.searchTerm, defaultState?.sorting, dispatch, location.pathname, state]);

  const setTableDataSorting = (sorting?: SortingState) => {
    setState({ ...state, sorting });
  };

  const setDataTablePage = (page: number) => {
    setState({ ...state, page });
  };

  const setDataTableSearchTerm = (searchTerm?: string) => {
    if (searchTerm != state.searchTerm) setState({ ...state, searchTerm, page: 1 });
  };

  const resetDataTableState = () => {
    setState(defaultState || {});
  };

  return [state, setTableDataSorting, setDataTablePage, setDataTableSearchTerm, resetDataTableState];
};
